import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Instagram, Linkedin, Facebook } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import FooterContactPanel from "@/components/FooterContactPanel";
import { PILLARS, SALES_EMAIL, WHATSAPP_URL, RERA_NUMBER, PHONE, PHONE_DISPLAY, TID } from "@/constants/testIds";

export default function Footer() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/eoi")) {
    // EOI landing has a minimal footer for compliance only
    return (
      <footer className="bg-void text-cream/70 py-8 text-xs text-center px-5">
        <div className="max-w-[900px] mx-auto space-y-2">
          <p>
            MahaRERA Registration No.{" "}
            <span data-testid={TID.footerRera}>{RERA_NUMBER}</span> · This is a
            standalone Expression of Interest page.
          </p>
          <p className="opacity-70">
            © {new Date().getFullYear()} DQUOR Spaces LLP · Pali Hill City
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-void text-cream relative overflow-hidden mt-24">
      {/* Editorial marquee strip */}
      <div className="border-b border-white/10 py-6 overflow-hidden">
        <div className="flex whitespace-nowrap marquee-track font-display text-3xl md:text-5xl text-cream/80">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 pr-10">
              <span>Peace with Nature</span>
              <span className="opacity-30">·</span>
              <span>Peace with Wealth</span>
              <span className="opacity-30">·</span>
              <span>Peace of Mind</span>
              <span className="opacity-30">·</span>
              <span>Peace with Family</span>
              <span className="opacity-30">·</span>
              <span>Peace with Time</span>
              <span className="opacity-30">·</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img
            src="/pali-hills-logo.png"
            alt="Pali Hills — Your Peace of Land"
            width={150}
            height={48}
            className="h-12 w-auto mb-6 brightness-0 invert opacity-90"
            draggable="false"
          />
          <div className="font-display text-3xl md:text-4xl leading-tight">
            The Peace of Land.
            <br />
            <span className="text-cream/60">A worldview, not a pitch.</span>
          </div>
          <p className="mt-6 text-cream/70 text-sm max-w-md leading-relaxed">
            A nature-centric layout of Non-Agricultural residential plots on a
            300-acre private hill near Pali, Maharashtra — 2 hours from Mumbai
            and Pune, 45 minutes from the Expressway, 1.5 hours from the
            upcoming Navi Mumbai International Airport.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={`tel:${PHONE}`}
              className="inline-flex items-center gap-2 text-sm text-cream/90 hover:text-lime transition-colors"
            >
              <Phone size={16} /> {PHONE_DISPLAY}
            </a>
            <a
              data-testid={TID.footerWhatsapp}
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-[#25D366] transition-colors"
            >
              <WhatsAppIcon size={16} className="text-[#25D366]" /> WhatsApp us
            </a>
            <a
              data-testid={TID.footerEmail}
              href={`mailto:${SALES_EMAIL}`}
              className="inline-flex items-center gap-2 text-sm text-cream/80 hover:text-lime transition-colors"
            >
              <Mail size={16} /> {SALES_EMAIL}
            </a>
            <span className="inline-flex items-center gap-2 text-sm text-cream/60">
              <MapPin size={16} /> Pali, Raigad, Maharashtra
            </span>
          </div>
        </div>

        <div className="md:col-span-3 md:col-start-7">
          <div className="eyebrow text-cream/60 mb-4">The Five Pillars</div>
          <ul className="space-y-3">
            {PILLARS.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/pillars/${p.slug}`}
                  className="text-cream/85 hover:text-lime transition-colors text-sm"
                >
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow text-cream/60 mb-4">Reach the site</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/connectivity" className="text-cream/85 hover:text-lime">Connectivity</Link></li>
            <li><Link to="/proof-of-peace" className="text-cream/85 hover:text-lime">Proof of Peace</Link></li>
            <li><Link to="/event" className="text-cream/85 hover:text-lime">Chapter III Launch</Link></li>
            <li><Link to="/blog" className="text-cream/85 hover:text-lime">Journal</Link></li>
            <li><Link to="/faq" className="text-cream/85 hover:text-lime">FAQ</Link></li>
            <li><Link to="/contact" className="text-cream/85 hover:text-lime">Book a Site Visit</Link></li>
          </ul>
        </div>
      </div>

      {/* Contact Us panel — visible on every non-admin page */}
      <div className="border-t border-white/10 px-5 md:px-10 py-14">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow text-cream/60">Contact Us</div>
            <h3 className="font-display text-3xl md:text-4xl text-cream mt-2 leading-tight">
              Have a question?
              <br />
              <span className="text-cream/60">We'll get back to you.</span>
            </h3>
            <p className="mt-4 text-cream/70 text-sm leading-relaxed max-w-md">
              Prefer to speak directly? Call, WhatsApp, or write in — someone
              from the Pali Hill City team is on the other end.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <a href={`tel:${PHONE}`} className="inline-flex items-center gap-2 text-cream/85 hover:text-lime transition-colors">
                <Phone size={15} /> {PHONE_DISPLAY}
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-cream/85 hover:text-[#25D366] transition-colors">
                <WhatsAppIcon size={15} className="text-[#25D366]" /> WhatsApp
              </a>
              <a href={`mailto:${SALES_EMAIL}`} className="inline-flex items-center gap-2 text-cream/85 hover:text-lime transition-colors">
                <Mail size={15} /> {SALES_EMAIL}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <FooterContactPanel />
          </div>
        </div>
      </div>

      {/* Compliance strip */}
      <div className="border-t border-white/10 px-5 md:px-10 py-8">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6 text-xs text-cream/60">
          <div className="max-w-3xl leading-relaxed">
            <span className="text-cream">MahaRERA Registration No.{" "}</span>
            <span data-testid={TID.footerRera}>{RERA_NUMBER}</span> · Pali Hill City
            is a nature-centric layout of Non-Agricultural residential plots
            registered with the Maharashtra Real Estate Regulatory Authority.
            No offer of guaranteed returns is made or implied. Please verify
            the RERA registration on the MahaRERA portal before any transaction.
          </div>
          <div className="flex items-center gap-4">
            <a aria-label="Instagram" href="#" className="text-cream/60 hover:text-lime"><Instagram size={18} /></a>
            <a aria-label="LinkedIn" href="#" className="text-cream/60 hover:text-lime"><Linkedin size={18} /></a>
            <a aria-label="Facebook" href="#" className="text-cream/60 hover:text-lime"><Facebook size={18} /></a>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6 text-xs text-cream/40 mt-6">
          <span>
            © {new Date().getFullYear()} DQUOR Spaces LLP · Pali Hill City.
            Photography is illustrative.
          </span>
          <div className="inline-flex items-center gap-3">
            <span className="text-cream/60 tracking-widest uppercase text-[10px]">Developed by</span>
            <img
              src="/malpani-logo-white.webp"
              alt="Malpani Infra"
              width={110}
              height={30}
              className="h-6 w-auto opacity-90 select-none"
              draggable="false"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
